import { HeadingSection } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useSession } from 'components/auth/useSession'
import { Breadcrumb } from 'components/breadcrumb'
import { PageContent } from 'components/layout/PageContent'
import { getLotacaoResponsavel } from 'components/lotacao-responsavel/utils/utils-lotacaoResponsavel'
import { TourTarget } from 'components/tour/components/TourTarget'
import { NacionalidadeEnum, TipoEnderecoEnum, TipoLocalNascimentoEnum } from 'graphql/types.generated'
import { useMemo, useState } from 'react'
import Permissions from 'types/Permissions.generated'

import { useSaudeIndigenaAuthorization } from '../authorization/useSaudeIndigenaAuthorization'
import { ModalStatusSincronizacaoCadsus } from './cadsus/components/ModalStatusSincronizacaoCadsus'
import { useCadsusCreateView } from './cadsus/hooks/useCadsusCreateView'
import { CidadaoForm } from './CidadaoForm'
import { convertCadsusToFormModel } from './converter-cidadao'
import { CidadaoTourIdEnum } from './tour-cidadao'
import { CidadaoFormModel, PAIS_BRASIL } from './types/model-cidadao'

export function CidadaoCreateView() {
  const { acesso, profissional } = useAcessoLotacaoOrEstagio()
  const { hasCadastrarEditarAldeadoPermission } = useSaudeIndigenaAuthorization()
  const { hasAuthorization } = useSession()
  const isUsuarioAps =
    acesso?.unidadeSaude?.tipo?.isTipoAps === true &&
    hasAuthorization(Permissions.gestaoDeCadastros.visualizarCadastroIndividual.cadastrarEEditar.cadastroIndividualAps)

  const defaultValues = useMemo(() => {
    const lotacaoLogada = getLotacaoResponsavel(profissional, acesso.cbo, acesso.equipe, acesso.unidadeSaude, acesso.id)
    const lotacaoPadrao = isUsuarioAps
      ? lotacaoLogada
      : {
          lotacao: {
            ...lotacaoLogada.lotacao,
            equipe: undefined,
          },
        }

    return {
      dadosPessoais: {
        nacionalidadeCidadao: {
          nacionalidade: NacionalidadeEnum.BRASILEIRA,
        },
        isCidadaoAldeado: hasCadastrarEditarAldeadoPermission,
        stNaoPossuiCpf: false,
        isCpfCadsus: false,
        stRegistroCadsus: false,
      },
      endereco: {
        pais: {
          id: PAIS_BRASIL.id,
          nome: PAIS_BRASIL.nome,
        },
      },
      tipoEndereco: hasCadastrarEditarAldeadoPermission ? TipoEnderecoEnum.ALDEIA : TipoEnderecoEnum.LOGRADOURO,
      cidadaoAldeado: hasCadastrarEditarAldeadoPermission
        ? {
            tipoLocalNascimento: TipoLocalNascimentoEnum.MUNICIPIO,
          }
        : undefined,
      responsabilidadeAcompanhamento: {
        responsavel: lotacaoPadrao,
      },
      informacoesSocioEconomicas: {
        openedAccordion: ['informacoesSocioEconomicas'],
      },
    } as CidadaoFormModel
  }, [
    acesso.cbo,
    acesso.equipe,
    acesso.id,
    acesso.unidadeSaude,
    hasCadastrarEditarAldeadoPermission,
    isUsuarioAps,
    profissional,
  ])

  const {
    cidadaoCadsusSessionStorage,
    syncCadsus,
    needLoginGovBr,
    isCadsusHabilitado,
    isModalLoading,
    cadsusSyncResult,
    syncCadsusState,
    setSyncCadsusState,
    hasCnsState,
  } = useCadsusCreateView()

  const [initialValues] = useState(() => {
    const lotacaoLogada = getLotacaoResponsavel(profissional, acesso.cbo, acesso.equipe, acesso.unidadeSaude, acesso.id)
    const lotacaoPadrao = isUsuarioAps
      ? lotacaoLogada
      : {
          lotacao: {
            ...lotacaoLogada.lotacao,
            equipe: undefined,
          },
        }

    return cidadaoCadsusSessionStorage
      ? convertCadsusToFormModel(cidadaoCadsusSessionStorage, lotacaoPadrao)
      : defaultValues
  })

  return (
    <PageContent type='filled'>
      <Breadcrumb title='Cadastro' />
      <TourTarget id={CidadaoTourIdEnum.PAGE_TITLE} inline>
        <HeadingSection level={1} title='Cadastro individual' />
      </TourTarget>
      <CidadaoForm
        initialValues={initialValues}
        sincronizarCadsus={syncCadsus}
        needLoginGovBr={needLoginGovBr}
        isCadsusHabilitado={isCadsusHabilitado}
        isUsuarioAps={isUsuarioAps}
        isAutoTourBlocked={syncCadsusState}
      />
      <ModalStatusSincronizacaoCadsus
        loading={isModalLoading}
        result={cadsusSyncResult}
        isOpen={syncCadsusState}
        isEdit={false}
        sincronizarCadsus={syncCadsus}
        setSyncModalState={setSyncCadsusState}
        hasCns={hasCnsState}
      />
    </PageContent>
  )
}
